/* ==========================================================================
   Retouromat Reviews – Frontend (Loox-Ersatz)
   Version 1.0

   Datenquelle: <script type="application/json" data-rr-data> im selben
   Custom Element. Das Liquid-Template rendert die ersten N Karten bereits
   serverseitig (SEO + LCP); dieses Skript uebernimmt danach Sortierung,
   Filter, "mehr laden" und die Lightbox ueber den vollstaendigen Datensatz.

   Review-Objekt (kurze Keys, um das Island klein zu halten):
     id  string   Metaobject-Handle
     r   number   Bewertung 1..5
     n   string   Anzeigename
     v   0|1      verifizierter Kauf
     d   string   ISO-Datum "YYYY-MM-DD"
     t   string   Fliesstext
     i   string[] Bild-URLs (Shopify CDN)
     a   string[] Alt-Texte, gleiche Reihenfolge wie i
     y   string   Antwort des Shops (optional)
   ========================================================================== */

(() => {
  'use strict';

  const STAR_PATH =
    'M12 2.4l2.9 5.9 6.5.9-4.7 4.6 1.1 6.5-5.8-3-5.8 3 1.1-6.5L2.6 9.2l6.5-.9L12 2.4z';

  const svg = () =>
    `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="${STAR_PATH}"/></svg>`;

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /** Sterne-Markup fuer einen Wert 0..5 (halbe Sterne werden unterstuetzt). */
  function stars(value, label) {
    let out = '';
    for (let i = 1; i <= 5; i++) {
      if (value >= i) out += `<span class="rr-star-on">${svg()}</span>`;
      else if (value >= i - 0.5) out += `<span class="rr-star-half">${svg()}${svg()}</span>`;
      else out += `<span class="rr-star-off">${svg()}</span>`;
    }
    const aria = label || `${value} / 5`;
    return `<span class="rr-stars" role="img" aria-label="${esc(aria)}">${out}</span>`;
  }

  /* Datumsformat folgt der Storefront-Sprache: auf shop.retouromat.nl steht
     <html lang="nl">, dort erscheint 14-8-2026 statt 14.8.2026. */
  let DATE_FMT = null;
  function dateFormatter() {
    if (DATE_FMT) return DATE_FMT;
    const lang = (document.documentElement.lang || 'de').trim() || 'de';
    try {
      DATE_FMT = new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'numeric', year: 'numeric' });
    } catch (e) {
      DATE_FMT = new Intl.DateTimeFormat('de', { day: 'numeric', month: 'numeric', year: 'numeric' });
    }
    return DATE_FMT;
  }

  function fmtDate(iso) {
    if (!iso) return '';
    const d = new Date(String(iso).slice(0, 10) + 'T12:00:00Z');
    return isNaN(d) ? '' : dateFormatter().format(d);
  }

  /* Texte des Einreichungsformulars, zweisprachig. */
  function FORM_T() {
    const nl = (document.documentElement.lang || '').startsWith('nl');
    return nl
      ? {
          pickStars: 'Kies een sterbeoordeling.',
          sending: 'Wordt verzonden …',
          thanks: 'Bedankt! Je beoordeling is binnengekomen en wordt na een korte controle gepubliceerd.',
          failed: 'Dat is helaas niet gelukt. Probeer het later nog eens of stuur ons een mail.',
          sent: 'Verzonden \u2713',
          offline: 'Geen verbinding met onze server. Controleer je internetverbinding en probeer het opnieuw.',
          err: {
            rating_invalid: 'Kies een sterbeoordeling van 1 tot 5.',
            name_invalid: 'Vul je naam in (minstens 2 tekens).',
            email_invalid: 'Controleer je e-mailadres.',
            text_too_short: 'Schrijf iets meer – minstens 10 tekens.',
            product_missing: 'Er ging intern iets mis. Laad de pagina opnieuw.',
            captcha_failed: 'De spamcontrole is mislukt. Laad de pagina opnieuw en probeer het nog eens.',
            save_failed: 'Opslaan is niet gelukt. Probeer het over een paar minuten nog eens.',
          },
        }
      : {
          pickStars: 'Bitte wähle eine Sternebewertung aus.',
          sending: 'Wird gesendet …',
          thanks: 'Danke! Deine Bewertung ist eingegangen und wird nach einer kurzen Prüfung veröffentlicht.',
          failed: 'Das hat leider nicht geklappt. Bitte versuche es später noch einmal oder schreib uns eine Mail.',
          sent: 'Gesendet \u2713',
          offline: 'Keine Verbindung zu unserem Server. Bitte prüfe deine Internetverbindung und versuch es erneut.',
          err: {
            rating_invalid: 'Bitte wähle 1 bis 5 Sterne aus.',
            name_invalid: 'Bitte trag deinen Namen ein (mindestens 2 Zeichen).',
            email_invalid: 'Bitte prüf deine E-Mail-Adresse.',
            text_too_short: 'Bitte schreib etwas mehr – mindestens 10 Zeichen.',
            product_missing: 'Da ist intern etwas schiefgelaufen. Bitte lade die Seite neu.',
            captcha_failed: 'Die Spam-Prüfung ist fehlgeschlagen. Bitte lade die Seite neu und versuch es noch einmal.',
            save_failed: 'Das Speichern hat nicht geklappt. Bitte versuch es in ein paar Minuten noch einmal.',
          },
        };
  }

  /** Shopify-CDN-URLs auf eine Zielbreite bringen; fremde URLs bleiben unveraendert. */
  function sized(url, width) {
    if (!url || !/cdn\.shopify\.com/.test(url)) return url;
    const [base, query] = url.split('?');
    const params = new URLSearchParams(query || '');
    params.set('width', String(width));
    return `${base}?${params.toString()}`;
  }

  const VERIFIED_ICON =
    '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l2.2 1.7 2.8-.3 1 2.6 2.4 1.4-.7 2.7.7 2.7-2.4 1.4-1 2.6-2.8-.3L12 22l-2.2-1.7-2.8.3-1-2.6-2.4-1.4.7-2.7-.7-2.7 2.4-1.4 1-2.6 2.8.3L12 2zm-1.1 13.2l5-5-1.3-1.3-3.7 3.7-1.8-1.8-1.3 1.3 3.1 3.1z"/></svg>';

  const PHOTO_ICON =
    '<svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M3.1 11.1l1.5-1.5 4 4 2.5-2.5 2.2 2.2V6.7H3.1v4.4zm-.7-5.9h11.6c.4 0 .7.3.7.8v11.6c0 .4-.3.7-.7.7H2.4a.7.7 0 0 1-.7-.7V6c0-.5.3-.8.7-.8zm8.4 5.1a1.1 1.1 0 1 1 0-2.2 1.1 1.1 0 0 1 0 2.2z"/><path d="M15.5 9.4l1.2 1.4.6-6.6L7.2 3.2l-.2 1.3H5.6l.2-2.2a.7.7 0 0 1 .8-.6l11.6 1.1c.4.1.7.4.6.8l-1.1 11.6a.7.7 0 0 1-.8.7l-1.4-.2V9.4z"/></svg>';

  const CLOSE_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M5 5l14 14M19 5L5 19"/></svg>';
  const PREV_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 5l-7 7 7 7"/></svg>';
  const NEXT_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 5l7 7-7 7"/></svg>';

  const TEXT_CLAMP = 260; // Zeichen, ab denen "Mehr anzeigen" erscheint

  /* ======================================================================
     <rr-reviews> - das Raster
     ====================================================================== */

  class RrReviews extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.all = this._readData();
      this.pageSize = Number(this.dataset.pageSize || 12) || 12;
      // Gesamtzahl kommt aus dem Produkt-Metafeld und ist sofort korrekt,
      // auch wenn erst die erste Seite geladen ist.
      this.totalCount = Number(this.dataset.totalCount || 0) || this.all.length;
      this.shopName = this.dataset.shopName || 'uns';

      // Beliebtheit: Werte kommen aus dem Produkt-Metafeld, der Startbonus
      // wird hier aus dem Datum berechnet, damit er auch zwischen zwei
      // Cron-Laeufen aktuell ist.
      this.scores = this._readScores();
      this.newBonus = Number(this.dataset.newBonus || 0) || 0;
      // Ab wie vielen Sternen eine Bewertung mit Foto nach oben darf.
      this.featuredMinRating = Number(this.dataset.featuredMinRating || 4) || 4;
      // Handverlesene Bewertungen: stehen in der Sortierung "Empfohlen" ganz
      // vorn, in genau der Reihenfolge, in der sie eingetragen sind.
      this.pinRank = new Map(
        (this.dataset.pinned || '')
          .split(',')
          .map((h) => h.trim())
          .filter(Boolean)
          .map((h, i) => [h, i])
      );
      this.viewEndpoint = this.dataset.viewEndpoint || '';

      // Uebersetzbare Texte (Sektions-Einstellungen, per Translate & Adapt)
      const d = this.dataset;
      const NL = (document.documentElement.lang || '').startsWith('nl');
      this.t = {
        reviews: d.tReviews || (NL ? 'beoordelingen' : 'Bewertungen'),
        reviewOne: d.tReviewOne || (NL ? 'beoordeling' : 'Bewertung'),
        of: d.tOf || (NL ? 'van' : 'von'),
        verified: d.tVerified || (NL ? 'Geverifieerd' : 'Verifiziert'),
        showMore: d.tShowMore || (NL ? 'Meer tonen' : 'Mehr anzeigen'),
        more: d.tMore || (NL ? 'Meer beoordelingen tonen' : 'Mehr Bewertungen anzeigen'),
        reply: d.tReply || (NL ? 'Reactie' : 'Antwort'),
        translated: d.tTranslated || '',
        // Eigener Hinweis fuer niederlaendische Originale (Feld "ol" der Bewertung).
        translatedNl: d.tTranslatedNl || '',
        showOriginal: d.tShowOriginal || (NL ? 'Origineel tonen' : 'Original anzeigen'),
        photoAlt: NL ? 'Klantfoto bij de beoordeling van' : 'Kundenfoto zur Bewertung von',
      };
      this.shown = this.pageSize;
      this.sort = this.dataset.defaultSort || 'best';
      this.filterRating = 0;
      this.photosOnly = false;

      this.gridEl = this.querySelector('[data-rr-grid]');
      this.moreEl = this.querySelector('[data-rr-more]');
      this.countEl = this.querySelector('[data-rr-count]');
      this.emptyEl = this.querySelector('[data-rr-empty]');
      if (!this.gridEl) return;

      this._bindHeader();
      this._bindGrid();
      this.render();
      this._loadRemainingPages();
    }

    _readScores() {
      try {
        const raw = this.dataset.scores;
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
      } catch (err) {
        return {};
      }
    }

    /**
     * Beliebtheit einer Bewertung.
     *
     * Der gespeicherte Wert ist die Summe der Aufrufe, die taeglich mit
     * Faktor 0,977 abklingt (Halbwertszeit 30 Tage). Dazu kommt ein
     * Startbonus, der sich alle 7 Tage halbiert.
     *
     * Ohne den Bonus haette eine frisch freigegebene Bewertung den Wert 0,
     * stuende ganz unten und koennte nie Aufrufe sammeln -- die Reihenfolge
     * wuerde nach einigen Monaten einfrieren.
     */
    /**
     * 1 = „guter Beleg“: Bewertung hat mindestens ein Foto UND genug Sterne.
     * Diese Bewertungen stehen in der Sortierung „best“ geschlossen oben,
     * innerhalb der Gruppe entscheidet weiter die Beliebtheit.
     */
    _featured(r) {
      const photos = r.i ? r.i.length : 0;
      if (!photos) return 0;
      return (r.r || 0) >= this.featuredMinRating ? 1 : 0;
    }

    _popularity(r) {
      const base = this.scores[r.id] || 0;
      if (!this.newBonus || !r.d) return base;
      const ageDays = (Date.now() - new Date(r.d + 'T12:00:00Z').getTime()) / 86400000;
      if (!isFinite(ageDays) || ageDays < 0) return base + this.newBonus;
      return base + this.newBonus * Math.pow(0.5, ageDays / 7);
    }

    _readData() {
      const tag = this.querySelector('script[data-rr-data]');
      if (!tag) return [];
      try {
        const rows = JSON.parse(tag.textContent || '[]');
        return Array.isArray(rows) ? rows.filter((r) => r && r.r) : [];
      } catch (err) {
        console.warn('[rr-reviews] Daten nicht lesbar:', err);
        return [];
      }
    }

    /* ---------------- Kopfzeile: Verteilung, Sortierung, Filter ---------- */

    _bindHeader() {
      const summary = this.querySelector('[data-rr-summary]');
      const dist = this.querySelector('[data-rr-dist]');
      if (summary && dist) {
        summary.addEventListener('click', () => {
          const open = summary.getAttribute('aria-expanded') === 'true';
          summary.setAttribute('aria-expanded', String(!open));
          dist.hidden = open;
        });
      }

      this.querySelectorAll('[data-rr-filter-rating]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const value = Number(btn.dataset.rrFilterRating);
          this.filterRating = this.filterRating === value ? 0 : value;
          this.shown = this.pageSize;
          this._syncPressed();
          this.render();
        });
      });

      const photos = this.querySelector('[data-rr-filter-photos]');
      if (photos) {
        photos.addEventListener('click', () => {
          this.photosOnly = !this.photosOnly;
          this.shown = this.pageSize;
          this._syncPressed();
          this.render();
        });
      }

      const reset = this.querySelector('[data-rr-reset]');
      if (reset) {
        reset.addEventListener('click', () => {
          this.filterRating = 0;
          this.photosOnly = false;
          this.shown = this.pageSize;
          this._syncPressed();
          this.render();
        });
      }

      const sortSel = this.querySelector('[data-rr-sort]');
      if (sortSel) {
        sortSel.addEventListener('change', () => {
          this.sort = sortSel.value;
          this.shown = this.pageSize;
          this.render();
        });
      }

      let rrResizeTimer = null;
      window.addEventListener('resize', () => {
        clearTimeout(rrResizeTimer);
        rrResizeTimer = setTimeout(() => {
          if (this.gridEl.dataset.cols !== String(this._colCount())) this._layout();
        }, 150);
      });

      if (this.moreEl) {
        this.moreEl.addEventListener('click', () => {
          this.shown += this.pageSize;
          this.render({ keepScroll: true });
        });
      }
    }

    /**
     * Liquid-Schleifen sind begrenzt, deshalb liefert die Sektion maximal
     * 250 Bewertungen pro Seite. Gibt es mehr, holen wir die Folgeseiten
     * still ueber die Section Rendering API nach. Erst danach sind
     * Sortierung und Filter ueber den kompletten Bestand korrekt.
     *
     * Wir laufen bewusst ueber feste Seitenzahlen (2..totalPages) statt
     * einem "naechste Seite"-Verweis zu folgen: der Verweis traegt in
     * Shopify ein Anzeige-Label, keine Seitenzahl.
     */
    async _loadRemainingPages() {
      const sectionId = this.dataset.sectionId;
      const totalPages = Number(this.dataset.totalPages || 1) || 1;
      const pageParam = this.dataset.pageParam || 'page';
      if (!sectionId || totalPages < 2) return;

      const seen = new Set(this.all.map((r) => r.id));
      let loadedAny = false;

      for (let page = 2; page <= totalPages; page++) {
        let html;
        try {
          const url = `${location.pathname}?section_id=${encodeURIComponent(sectionId)}&${encodeURIComponent(pageParam)}=${page}`;
          const res = await fetch(url, { headers: { accept: 'text/html' } });
          if (!res.ok) continue;
          html = await res.text();
        } catch (err) {
          console.warn('[rr-reviews] Seite ' + page + ' nicht ladbar:', err);
          continue;
        }

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const tag = doc.querySelector('script[data-rr-data]');
        if (!tag) continue;

        let rows = [];
        try {
          rows = JSON.parse(tag.textContent || '[]');
        } catch (err) {
          continue;
        }

        // Eine Seite ohne passende Bewertungen ist voellig normal --
        // metaobjects.rr_review.values enthaelt alle Produkte.
        // Deshalb hier NICHT abbrechen, sondern weiterblaettern.
        for (const r of rows) {
          if (!r || !r.r || seen.has(r.id)) continue;
          seen.add(r.id);
          this.all.push(r);
          loadedAny = true;
        }
      }

      if (loadedAny) this.render({ keepScroll: true });
    }

    _syncPressed() {
      this.querySelectorAll('[data-rr-filter-rating]').forEach((b) => {
        b.setAttribute('aria-pressed', String(Number(b.dataset.rrFilterRating) === this.filterRating));
      });
      const photos = this.querySelector('[data-rr-filter-photos]');
      if (photos) photos.setAttribute('aria-pressed', String(this.photosOnly));
      const reset = this.querySelector('[data-rr-reset]');
      if (reset) reset.hidden = !this.filterRating && !this.photosOnly;
    }

    /* ---------------- Grid-Interaktion ----------------------------------- */

    _bindGrid() {
      this.gridEl.addEventListener('click', (ev) => {
        // Original ein-/ausblenden. Muss vor der Lightbox geprueft werden,
        // sonst wuerde der Klick die Karte oeffnen.
        const orig = ev.target.closest('[data-rr-original]');
        if (orig) {
          ev.stopPropagation();
          const box = orig.closest('.rr-item').querySelector('.rr-item__original');
          if (box) box.hidden = !box.hidden;
          return;
        }
        const more = ev.target.closest('.rr-item__more');
        if (more) {
          const card = more.closest('.rr-item');
          const full = card.querySelector('.rr-item__text');
          full.textContent = full.dataset.full;
          more.remove();
          return;
        }
        const card = ev.target.closest('.rr-item--photo');
        if (card) {
          const id = card.dataset.id;
          const review = this.all.find((r) => r.id === id);
          if (review) {
            this._countView(review.id);
            lightbox.open(review, 0, this.t);
          }
        }
      });
    }

    /**
     * Zaehlt das Oeffnen einer Bewertung -- hoechstens einmal je Besucher
     * und Sitzung, damit wiederholtes Klicken die Reihenfolge nicht
     * verschiebt. Faehrt still, wenn kein Endpunkt gesetzt ist.
     */
    _countView(id) {
      if (!this.viewEndpoint || !id) return;
      const KEY = 'rr-viewed';
      let seen = [];
      try {
        seen = JSON.parse(sessionStorage.getItem(KEY) || '[]');
        if (seen.includes(id)) return;
        seen.push(id);
        sessionStorage.setItem(KEY, JSON.stringify(seen.slice(-200)));
      } catch (err) {
        // Privater Modus o. ae. -- dann zaehlen wir eben doppelt.
      }
      const body = JSON.stringify({ id });
      try {
        if (navigator.sendBeacon) {
          navigator.sendBeacon(this.viewEndpoint, new Blob([body], { type: 'application/json' }));
        } else {
          fetch(this.viewEndpoint, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body,
            keepalive: true,
          }).catch(() => {});
        }
      } catch (err) {
        /* Zaehlung ist nie wichtiger als die Seite */
      }
    }

    /* ---------------- Datenaufbereitung ---------------------------------- */

    visible() {
      let rows = this.all;
      if (this.filterRating) rows = rows.filter((r) => Math.round(r.r) === this.filterRating);
      if (this.photosOnly) rows = rows.filter((r) => r.i && r.i.length);

      const byDate = (a, b) => String(b.d || '').localeCompare(String(a.d || ''));
      const sorted = rows.slice();
      if (this.sort === 'best') {
        const pin = (r) =>
          this.pinRank.has(r.id) ? this.pinRank.get(r.id) : Number.MAX_SAFE_INTEGER;
        sorted.sort(
          (a, b) =>
            pin(a) - pin(b) ||
            this._featured(b) - this._featured(a) ||
            this._popularity(b) - this._popularity(a) ||
            byDate(a, b)
        );
      }
      else if (this.sort === 'popular')
        sorted.sort((a, b) => this._popularity(b) - this._popularity(a) || byDate(a, b));
      else if (this.sort === 'recent') sorted.sort(byDate);
      else if (this.sort === 'oldest') sorted.sort((a, b) => -byDate(a, b));
      else if (this.sort === 'highest') sorted.sort((a, b) => b.r - a.r || byDate(a, b));
      else if (this.sort === 'lowest') sorted.sort((a, b) => a.r - b.r || byDate(a, b));
      else if (this.sort === 'photos')
        sorted.sort((a, b) => (b.i ? b.i.length : 0) - (a.i ? a.i.length : 0) || byDate(a, b));
      return sorted;
    }

    _colCount() {
      const raw = getComputedStyle(this.gridEl).getPropertyValue('--rr-cols');
      const n = parseInt(raw, 10);
      return n > 0 ? n : 1;
    }

    /**
     * Verteilt die Karten auf feste Spalten statt auf CSS-Mehrspalten.
     * CSS-Spalten laufen unten ausgefranst aus - je nach Kartenhoehe steht am
     * Ende eine Karte allein. Hier bekommt immer die aktuell kuerzeste Spalte
     * die naechste Karte, dadurch enden alle Spalten praktisch gleich hoch.
     * Ablauf: erst reihum einfuegen (damit die Hoehen bei Spaltenbreite
     * gemessen werden), dann anhand der echten Hoehen neu verteilen.
     */
    _layout() {
      const grid = this.gridEl;
      const cols = this._colCount();
      const items = Array.from(grid.querySelectorAll('.rr-item'));
      if (!items.length) {
        grid.innerHTML = '';
        grid.dataset.cols = String(cols);
        return;
      }

      const make = () => {
        const out = [];
        for (let i = 0; i < cols; i++) {
          const d = document.createElement('div');
          d.className = 'rr-col';
          out.push(d);
        }
        return out;
      };

      // Durchgang 1: reihum, nur um korrekte Hoehen zu bekommen
      let colEls = make();
      items.forEach((el, i) => colEls[i % cols].appendChild(el));
      grid.replaceChildren(...colEls);

      if (cols > 1) {
        const heights = items.map((el) => el.offsetHeight || 1);
        // Wie viele der vordersten Karten sind handverlesen? Die bleiben
        // reihum verteilt, damit sie in der eingetragenen Reihenfolge
        // gelesen werden. Erst danach wird nach Hoehe ausgeglichen.
        let pinCount = 0;
        while (pinCount < items.length && this.pinRank.has(items[pinCount].dataset.id)) {
          pinCount++;
        }
        colEls = make();
        const fill = new Array(cols).fill(0);
        items.forEach((el, i) => {
          let k;
          if (i < pinCount) {
            k = i % cols;
          } else {
            k = 0;
            for (let j = 1; j < cols; j++) if (fill[j] < fill[k]) k = j;
          }
          colEls[k].appendChild(el);
          fill[k] += heights[i];
        });

        // Feinausgleich fuer einen sauberen unteren Abschluss: solange es die
        // Spanne zwischen laengster und kuerzester Spalte echt verkleinert,
        // wandert die jeweils letzte Karte der laengsten Spalte ans Ende der
        // kuerzesten. Die Spanne sinkt in jedem Durchgang streng monoton,
        // deshalb kann das nicht hin- und herpendeln. Angeheftete Karten
        // bleiben unangetastet.
        const colItems = colEls.map((c) => Array.from(c.children));
        const idx = new Map(items.map((el, i) => [el, i]));
        const spread = () => {
          let mx = fill[0];
          let mn = fill[0];
          for (let j = 1; j < cols; j++) {
            if (fill[j] > mx) mx = fill[j];
            if (fill[j] < mn) mn = fill[j];
          }
          return mx - mn;
        };
        for (let pass = 0; pass < 60; pass++) {
          let lo = 0;
          for (let j = 1; j < cols; j++) if (fill[j] < fill[lo]) lo = j;
          // Hoechste Spalte, deren letzte Karte ueberhaupt verschoben werden
          // darf. Eine angeheftete Karte am Ende darf den Ausgleich nicht
          // komplett blockieren -- dann wird eben eine andere Spalte gewaehlt.
          let hi = -1;
          for (let j = 0; j < cols; j++) {
            if (j === lo) continue;
            const last = colItems[j][colItems[j].length - 1];
            if (!last || this.pinRank.has(last.dataset.id)) continue;
            if (hi === -1 || fill[j] > fill[hi]) hi = j;
          }
          if (hi === -1) break;
          const list = colItems[hi];
          const el = list[list.length - 1];
          const hc = heights[idx.get(el)] || 1;
          const before = spread();
          fill[hi] -= hc;
          fill[lo] += hc;
          if (spread() >= before) {
            fill[hi] += hc;
            fill[lo] -= hc;
            break;
          }
          list.pop();
          colItems[lo].push(el);
        }
        colEls.forEach((c, j) => c.replaceChildren(...colItems[j]));
        grid.replaceChildren(...colEls);
      }
      grid.dataset.cols = String(cols);
    }

    render({ keepScroll = false } = {}) {
      const rows = this.visible();
      const slice = rows.slice(0, this.shown);
      const y = keepScroll ? window.scrollY : null;

      this.gridEl.innerHTML = slice.map((r) => this._card(r)).join('');
      this._layout();
      this._relayoutOnLoad();

      if (this.countEl) {
        const total = Math.max(this.totalCount, this.all.length);
        const word = total === 1 ? this.t.reviewOne : this.t.reviews;
        this.countEl.textContent =
          rows.length === total
            ? `${rows.length} ${word}`
            : `${rows.length} ${this.t.of} ${total} ${word}`;
      }
      if (this.emptyEl) this.emptyEl.hidden = rows.length > 0;
      if (this.moreEl) {
        const rest = rows.length - slice.length;
        this.moreEl.hidden = rest <= 0;
        this.moreEl.textContent =
          rest > 0 ? `${this.t.more} (${rest})` : this.t.more;
      }
      if (y !== null) window.scrollTo({ top: y });
    }

    /**
     * Sicherheitsnetz: Bilder ohne bekanntes Seitenverhaeltnis aendern beim
     * Laden die Kartenhoehe. Danach wird einmal neu ausgeglichen, damit der
     * untere Abschluss sauber bleibt.
     */
    _relayoutOnLoad() {
      const imgs = Array.from(this.gridEl.querySelectorAll('img')).filter((im) => !im.complete);
      if (!imgs.length) return;
      const bump = () => {
        clearTimeout(this._loadTimer);
        this._loadTimer = setTimeout(() => this._layout(), 150);
      };
      imgs.forEach((im) => {
        im.addEventListener('load', bump, { once: true });
        im.addEventListener('error', bump, { once: true });
      });
    }

    _card(r) {
      const hasImg = r.i && r.i.length;
      const long = r.t && r.t.length > TEXT_CLAMP;
      const shown = long ? r.t.slice(0, TEXT_CLAMP).replace(/\s+\S*$/, '') + '…' : r.t || '';
      const alt = (r.a && r.a[0]) || `${this.t.photoAlt} ${r.n}`;

      // Seitenverhaeltnis mitgeben: sonst ist die Karte beim Umbruch noch
      // 0 Pixel hoch und die Spalten werden auf falschen Hoehen verteilt.
      const ar = (r.ar && Number(r.ar[0])) || 0;
      const dim = ar > 0 ? ` width="480" height="${Math.round(480 / ar)}"` : '';

      const media = hasImg
        ? `<div class="rr-item__media">
             <img src="${esc(sized(r.i[0], 480))}" alt="${esc(alt)}"${dim} loading="lazy" decoding="async">
             ${r.i.length > 1 ? `<span class="rr-item__badge">${PHOTO_ICON}+${r.i.length - 1}</span>` : ''}
           </div>`
        : '';

      return `<article class="rr-item${hasImg ? ' rr-item--photo' : ''}" data-id="${esc(r.id)}">
        ${media}
        <div class="rr-item__body">
          <div class="rr-item__author">${esc(r.n)}${r.v ? `<span class="rr-verified">${VERIFIED_ICON}${esc(this.t.verified)}</span>` : ''}</div>
          <div class="rr-item__date">${esc(fmtDate(r.d))}</div>
          <div class="rr-item__stars">${stars(r.r)}</div>
          <p class="rr-item__text" data-full="${esc(r.t || '')}">${esc(shown)}</p>
          ${long ? `<button type="button" class="rr-item__more">${esc(this.t.showMore)}</button>` : ''}
          ${
            r.o && this.t.translated
              ? `<p class="rr-item__translated">${esc(r.ol === 'nl' && this.t.translatedNl ? this.t.translatedNl : this.t.translated)}
                   <button type="button" class="rr-item__more" data-rr-original>${esc(this.t.showOriginal)}</button>
                 </p>
                 <p class="rr-item__text rr-item__original" hidden>${esc(r.o)}</p>`
              : ''
          }
          ${r.y ? `<div class="rr-item__reply"><strong>${esc(this.t.reply)}</strong>${esc(r.y)}</div>` : ''}
        </div>
      </article>`;
    }
  }

  /* ======================================================================
     Lightbox - ein einziges <dialog> fuer die ganze Seite
     ====================================================================== */

  const lightbox = {
    el: null, review: null, index: 0,

    ensure() {
      if (this.el) return this.el;
      const d = document.createElement('dialog');
      d.className = 'rr-lightbox rr';
      d.innerHTML = `
        <div class="rr-lightbox__inner">
          <div class="rr-lightbox__media">
            <button type="button" class="rr-lightbox__close" aria-label="Schliessen">${CLOSE_ICON}</button>
            <button type="button" class="rr-lightbox__nav rr-lightbox__nav--prev" aria-label="Vorheriges Bild">${PREV_ICON}</button>
            <img alt="">
            <button type="button" class="rr-lightbox__nav rr-lightbox__nav--next" aria-label="Naechstes Bild">${NEXT_ICON}</button>
            <div class="rr-lightbox__dots"></div>
          </div>
          <div class="rr-lightbox__side"></div>
        </div>`;
      document.body.appendChild(d);

      d.querySelector('.rr-lightbox__close').addEventListener('click', () => d.close());
      d.querySelector('.rr-lightbox__nav--prev').addEventListener('click', () => this.step(-1));
      d.querySelector('.rr-lightbox__nav--next').addEventListener('click', () => this.step(1));
      d.addEventListener('click', (ev) => { if (ev.target === d) d.close(); });
      d.addEventListener('keydown', (ev) => {
        if (ev.key === 'ArrowLeft') this.step(-1);
        if (ev.key === 'ArrowRight') this.step(1);
      });
      this.el = d;
      return d;
    },

    open(review, index, t) {
      const d = this.ensure();
      this.review = review;
      this.t = t || {};
      this.index = index || 0;
      this.paint();
      if (!d.open) d.showModal();
    },

    step(delta) {
      const n = (this.review && this.review.i && this.review.i.length) || 0;
      if (n < 2) return;
      this.index = (this.index + delta + n) % n;
      this.paint();
    },

    paint() {
      const r = this.review;
      if (!r) return;
      const d = this.el;
      const t = this.t || {};
      const imgs = r.i || [];
      const img = d.querySelector('.rr-lightbox__media img');
      img.src = sized(imgs[this.index], 1400);
      img.alt = (r.a && r.a[this.index]) || `${(this.t && this.t.photoAlt) || 'Kundenfoto zur Bewertung von'} ${r.n}`;

      const multi = imgs.length > 1;
      d.querySelector('.rr-lightbox__nav--prev').hidden = !multi;
      d.querySelector('.rr-lightbox__nav--next').hidden = !multi;
      d.querySelector('.rr-lightbox__dots').innerHTML = multi
        ? imgs.map((_, i) =>
            `<button type="button" class="rr-lightbox__dot" aria-current="${i === this.index}" aria-label="Bild ${i + 1}"></button>`).join('')
        : '';
      d.querySelectorAll('.rr-lightbox__dot').forEach((dot, i) => {
        dot.addEventListener('click', () => { this.index = i; this.paint(); });
      });

      d.querySelector('.rr-lightbox__side').innerHTML = `
        <div class="rr-item__author">${esc(r.n)}${r.v ? `<span class="rr-verified">${VERIFIED_ICON}${esc(t.verified || '')}</span>` : ''}</div>
        <div class="rr-item__date">${esc(fmtDate(r.d))}</div>
        <div class="rr-item__stars">${stars(r.r)}</div>
        <p class="rr-item__text">${esc(r.t || '')}</p>
        ${
          r.o && t.translated
            ? `<p class="rr-item__translated">${esc(r.ol === 'nl' && t.translatedNl ? t.translatedNl : t.translated)}
                 <button type="button" class="rr-item__more" data-rr-original>${esc(t.showOriginal || '')}</button>
               </p>
               <p class="rr-item__text rr-item__original" hidden>${esc(r.o)}</p>`
            : ''
        }
        ${r.y ? `<div class="rr-item__reply"><strong>${esc(t.reply || '')}</strong>${esc(r.y)}</div>` : ''}`;

      const origBtn = d.querySelector('[data-rr-original]');
      if (origBtn) {
        origBtn.addEventListener('click', () => {
          const orig = d.querySelector('.rr-item__original');
          orig.hidden = !orig.hidden;
        });
      }
    },
  };

  /* ======================================================================
     <rr-review-form> - Bewertung einreichen
     ====================================================================== */

  const MAX_FILES = 5;
  const MAX_BYTES = 8 * 1024 * 1024;

  class RrReviewForm extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.form = this.querySelector('form');
      this.endpoint = this.dataset.endpoint;
      this.files = [];
      if (!this.form) return;

      this._fillToken();
      this._bindStars();
      this._bindUpload();
      this.form.addEventListener('submit', (ev) => { ev.preventDefault(); this.submit(); });
    }

    /**
     * Der Bewertungslink aus der Klaviyo-Mail traegt ?t=<signierter Token>.
     * Liquid kommt an Query-Parameter nicht heran, also setzen wir das
     * versteckte Feld hier. Ohne Token funktioniert das Formular ebenfalls,
     * die Bewertung bekommt dann nur kein "Verifiziert"-Kennzeichen.
     */
    _fillToken() {
      const field = this.querySelector('[data-rr-token]');
      if (!field) return;
      try {
        field.value = new URLSearchParams(location.search).get('t') || '';
      } catch (err) {
        field.value = '';
      }
    }

    _bindStars() {
      const wrap = this.querySelector('.rr-rate');
      if (!wrap) return;
      const paint = (value) => {
        wrap.querySelectorAll('label').forEach((l, i) => l.classList.toggle('is-on', i < value));
      };
      wrap.querySelectorAll('input').forEach((input) => {
        input.addEventListener('change', () => paint(Number(input.value)));
      });
      wrap.querySelectorAll('label').forEach((label, i) => {
        label.addEventListener('mouseenter', () => paint(i + 1));
      });
      wrap.addEventListener('mouseleave', () => {
        const checked = wrap.querySelector('input:checked');
        paint(checked ? Number(checked.value) : 0);
      });
    }

    _bindUpload() {
      const drop = this.querySelector('[data-rr-drop]');
      const input = this.querySelector('input[type="file"]');
      const thumbs = this.querySelector('[data-rr-thumbs]');
      if (!drop || !input || !thumbs) return;

      const paint = () => {
        thumbs.innerHTML = this.files.map((f, i) =>
          `<div class="rr-thumb"><img src="${URL.createObjectURL(f)}" alt=""><button type="button" data-i="${i}" aria-label="Bild entfernen">&times;</button></div>`).join('');
        thumbs.querySelectorAll('button').forEach((b) =>
          b.addEventListener('click', () => { this.files.splice(Number(b.dataset.i), 1); paint(); }));
      };

      const add = (list) => {
        for (const f of list) {
          if (this.files.length >= MAX_FILES) break;
          if (!f.type.startsWith('image/')) continue;
          if (f.size > MAX_BYTES) {
            this.status(`"${f.name}" ist groesser als 8 MB und wurde uebersprungen.`, 'err');
            continue;
          }
          this.files.push(f);
        }
        paint();
      };

      drop.addEventListener('click', () => input.click());
      input.addEventListener('change', () => { add(input.files); input.value = ''; });
      ['dragenter', 'dragover'].forEach((e) =>
        drop.addEventListener(e, (ev) => { ev.preventDefault(); drop.classList.add('is-over'); }));
      ['dragleave', 'drop'].forEach((e) =>
        drop.addEventListener(e, (ev) => { ev.preventDefault(); drop.classList.remove('is-over'); }));
      drop.addEventListener('drop', (ev) => add(ev.dataTransfer.files));
    }

    status(msg, kind) {
      const box = this.querySelector('[data-rr-status]');
      if (!box) return;
      box.hidden = false;
      box.className = `rr-form__status rr-form__status--${kind || 'ok'}`;
      box.textContent = msg;
    }

    async submit() {
      const btn = this.form.querySelector('[type="submit"]');
      const data = new FormData(this.form);

      if (!data.get('rating')) {
        this.status(FORM_T().pickStars, 'err');
        return;
      }
      this.files.forEach((f) => data.append('photos', f, f.name));

      btn.disabled = true;
      const label = btn.textContent;
      btn.textContent = FORM_T().sending;

      const T = FORM_T();
      try {
        let res;
        try {
          res = await fetch(this.endpoint, { method: 'POST', body: data });
        } catch (netErr) {
          // fetch wirft nur bei Netzwerk-/CORS-Fehlern, nie bei HTTP 4xx/5xx.
          // Haeufigster Fall in der Praxis: die Seite laeuft nicht auf einer der
          // in STOREFRONT_ORIGINS eingetragenen Domains (z. B. im Theme-Editor,
          // der unter online-store-web.shopifyapps.com rendert).
          throw new Error('__offline__');
        }
        const body = await res.json().catch(() => ({}));
        if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
        // Das Formular bleibt stehen, wird aber stillgelegt: der Button zeigt
        // "Gesendet", alle Felder werden gesperrt. So sieht man, dass etwas
        // passiert ist - ein einfach verschwundenes Formular wirkt wie ein Fehler.
        btn.textContent = T.sent;
        btn.classList.add('rr-btn--done');
        btn.disabled = true;
        this.classList.add('rr-form-done');
        this.form
          .querySelectorAll('input, textarea, button, [data-rr-drop]')
          .forEach((el) => {
            el.disabled = true;
            el.setAttribute('aria-disabled', 'true');
          });
        this.status(T.thanks, 'ok');
        const box = this.querySelector('[data-rr-status]');
        if (box && box.scrollIntoView) box.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (err) {
        const code = (err && err.message) || '';
        let msg = T.failed;
        if (code === '__offline__') msg = T.offline;
        else if (T.err && T.err[code]) msg = T.err[code];
        this.status(msg, 'err');
        btn.disabled = false;
        btn.textContent = label;
      }
    }
  }

  /* ---------------- Registrierung -------------------------------------- */

  if (!customElements.get('rr-reviews')) customElements.define('rr-reviews', RrReviews);
  if (!customElements.get('rr-review-form')) customElements.define('rr-review-form', RrReviewForm);

  // Rating-Badge unter dem Produkttitel scrollt zum Raster
  document.addEventListener('click', (ev) => {
    const badge = ev.target.closest('[data-rr-scroll-to]');
    if (!badge) return;
    const target = document.querySelector(badge.dataset.rrScrollTo);
    if (!target) return;
    ev.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
})();
